import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './PaymentSuccess.css';

// Create a separate axios instance for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
});

const PaymentSuccess = () => {
  const { confirmationNumber } = useParams();
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConfirmationDetails();
  }, [confirmationNumber]);

  const fetchConfirmationDetails = async () => {
    try {
      const response = await publicAxios.get(`/public/payment-confirmation/${confirmationNumber}`);
      setConfirmationDetails(response.data);
      setLoading(false);
    } catch (err) {
      setError('Unable to load confirmation details');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-success-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading confirmation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h1>Error</h1>
          <p>{error}</p>
          <p>Confirmation Number: {confirmationNumber}</p>
        </div>
      </div>
    );
  }

  const remainingBalance = confirmationDetails && confirmationDetails.paymentType === 'deposit' 
    ? (parseFloat(confirmationDetails.totalAmount) - parseFloat(confirmationDetails.amount)).toFixed(2)
    : null;

  return (
    <div className="payment-success-page">
      <div className="success-container">
        <div className="success-header">
          <div className="success-icon">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#4CAF50"/>
              <path d="M55 30L35 50L25 40" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>Payment Successful!</h1>
          <p className="success-subtitle">Your payment has been processed successfully</p>
        </div>

        <div className="confirmation-card">
          <div className="confirmation-header">
            <h2>Confirmation Details</h2>
          </div>
          
          <div className="confirmation-details">
            <div className="detail-row">
              <span className="detail-label">Confirmation Number</span>
              <span className="detail-value confirmation-number">{confirmationNumber}</span>
            </div>

            {confirmationDetails && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Quote Number</span>
                  <span className="detail-value">{confirmationDetails.quoteNumber}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Trip</span>
                  <span className="detail-value">{confirmationDetails.quoteTitle}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Payment Type</span>
                  <span className="detail-value">
                    {confirmationDetails.paymentType === 'deposit' ? 'Deposit' : 'Full Payment'}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Amount Paid</span>
                  <span className="detail-value amount">
                    ${parseFloat(confirmationDetails.amount).toFixed(2)}
                  </span>
                </div>

                {remainingBalance && (
                  <div className="detail-row remaining-balance">
                    <span className="detail-label">Remaining Balance</span>
                    <span className="detail-value">${remainingBalance}</span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">Payment Date</span>
                  <span className="detail-value">
                    {new Date(confirmationDetails.paymentDate).toLocaleDateString()}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="next-steps-card">
          <h3>What's Next?</h3>
          <ul className="next-steps-list">
            <li>
              <span className="step-icon">📧</span>
              <span>A confirmation email has been sent to your email address</span>
            </li>
            <li>
              <span className="step-icon">📞</span>
              <span>Your travel agent will contact you within 24-48 hours</span>
            </li>
            {remainingBalance && (
              <li>
                <span className="step-icon">💳</span>
                <span>Your remaining balance of ${remainingBalance} will be due before your travel date</span>
              </li>
            )}
            <li>
              <span className="step-icon">✈️</span>
              <span>Start preparing for your amazing trip!</span>
            </li>
          </ul>
        </div>

        {confirmationDetails && (
          <div className="agent-info-card">
            <h3>Your Travel Agent</h3>
            <div className="agent-details">
              <p className="agent-name">{confirmationDetails.agentName}</p>
              {confirmationDetails.agencyName && (
                <p className="agency-name">{confirmationDetails.agencyName}</p>
              )}
              {confirmationDetails.agencyPhone && (
                <p className="contact-info">
                  <span className="icon">📞</span> {confirmationDetails.agencyPhone}
                </p>
              )}
              {confirmationDetails.agencyEmail && (
                <p className="contact-info">
                  <span className="icon">✉️</span> {confirmationDetails.agencyEmail}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="action-buttons">
          <button 
            className="print-button"
            onClick={() => window.print()}
          >
            Print Confirmation
          </button>
          <Link to="/" className="home-button">
            Return to Home
          </Link>
        </div>

        <div className="footer-note">
          <p>Need help? Contact us at support@tpcrm.io</p>
          <p>Thank you for choosing Travel+ CRM for your travel needs!</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
